import { cva } from "class-variance-authority";

export { default as Badge } from "./Badge.vue";
export { default as ChecklistBadge } from "./Checklist.vue";
export { default as Status } from "./Status.vue";
export { default as Id } from "./Id.vue";
export { default as Visibility } from "./Visibility.vue";
export { default as Priority } from "./Priority.vue";
export { default as Recurring } from "./Recurring.vue";

import { colors as _colors } from "./colors.js";

export const types = ["color", "outline", "full", "light", "modern", "id"];
export const colors = [
    "gray",
    "brand",
    "error",
    "warning",
    "success",
    "gray-blue",
    "blue-light",
    "blue",
    "indigo",
    "purple",
    "pink",
    "orange",
];
export const sizes = ["sm", "md", "lg"];
export const icons = [
    true,
    false,
    "dot",
    "avatar",
    "close",
    "trailing",
    "leading",
    "only",
];

export const variants = cva(
    [
        "flex",
        "items-center",
        "shrink-0",
        "[&_i]:h-3.5",
        "[&_i]:w-3.5",
        "[&_i]:shrink-0",
        "max-w-full",
        "select-none",
        "whitespace-nowrap",
    ],
    {
        variants: {
            variant: {
                pill: ["rounded-full"],
            },
            type: {
                color: ["border"],
                outline: ["border-[1.5px]"],
                full: ["text-white", "[&_i]:fg-white"],
                modern: [
                    "bg-primary",
                    "border",
                    "border-primary",
                    "text-secondary",
                ],
                id: [
                    "bg-primary",
                    "border",
                    "border-primary",
                    "text-secondary",
                    "font-roboto-mono",
                ],
            },
            border: {
                dashed: ["border-dashed"],
            },
            color: {
                gray: ["[&_i]:text-utility-gray-600"],
                brand: ["[&_i]:text-utility-brand-600"],
                error: ["[&_i]:text-utility-error-600"],
                warning: ["[&_i]:text-utility-warning-600"],
                success: ["[&_i]:text-utility-success-600"],
                "gray-blue": ["[&_i]:text-utility-gray-blue-500"],
                "blue-light": ["[&_i]:text-utility-blue-light-500"],
                blue: ["[&_i]:text-utility-blue-500"],
                indigo: ["[&_i]:text-utility-indigo-500"],
                purple: ["[&_i]:text-utility-purple-500"],
                pink: ["[&_i]:text-utility-pink-500"],
                orange: ["[&_i]:text-utility-orange-500"],
            },
            size: {
                sm: [
                    "h-5.5",
                    "py-0.5",
                    "px-1.5",
                    "gap-1",
                    "text-xs",
                    "leading-xs",
                ],
                md: [
                    "h-6",
                    "py-0.5",
                    "px-2",
                    "gap-1.5",
                    "text-sm",
                    "leading-sm",
                ],
                lg: [
                    "h-7",
                    "py-1",
                    "px-2.5",
                    "gap-1.5",
                    "text-sm",
                    "leading-sm",
                ],
            },
            icon: {
                dot: ["[&_i]:h-2", "[&_i]:w-2"],
                close: [
                    "gap-0.5",
                    "[&_i]:h-4",
                    "[&_i]:w-4",
                    "[&_i]:p-0.5",
                    "[&_i]:cursor-pointer",
                ],
                trailing: ["gap-1"],
                leading: ["gap-1"],
                only: ["justify-center"],
            },
            number: {
                true: ["justify-center"],
            },
            disabled: {
                true: ["opacity-50", "cursor-not-allowed"],
            },
        },
        compoundVariants: [
            {
                size: ["sm"],
                type: ["id"],
                class: ["text-[10px]"],
            },
            {
                size: ["md"],
                type: ["id"],
                class: ["text-[11px]"],
            },
            {
                size: ["lg"],
                type: ["id"],
                class: ["text-[13px]"],
            },
            {
                variant: "pill",
                size: ["sm"],
                class: ["px-2"],
            },
            {
                variant: "pill",
                size: ["md"],
                class: ["px-2.5"],
            },
            {
                number: true,
                size: "sm",
                class: ["min-w-[22px]"],
            },
            {
                number: true,
                size: "md",
                class: ["min-w-[24px]"],
            },
            {
                number: true,
                size: "lg",
                class: ["min-w-[28px]"],
            },
            {
                variant: "pill",
                size: ["lg"],
                class: ["px-3"],
            },
            {
                variant: "pill",
                size: ["sm"],
                icon: ["dot", "leading"],
                class: ["ltr:pl-1.5", "rtl:pr-1.5"],
            },
            {
                variant: "pill",
                size: ["md"],
                icon: ["dot", "leading"],
                class: ["ltr:pl-2", "rtl:pr-2"],
            },
            {
                variant: "pill",
                size: ["lg"],
                icon: ["dot", "leading"],
                class: ["ltr:pl-2.5", "rtl:pr-2.5"],
            },
            {
                variant: "pill",
                size: ["sm"],
                icon: ["avatar"],
                class: ["ltr:pl-[3px]", "rtl:pr-[3px]"],
            },
            {
                variant: "pill",
                size: ["md"],
                icon: ["avatar"],
                class: ["ltr:pl-1", "rtl:pr-1"],
            },
            {
                variant: "pill",
                size: ["lg"],
                icon: ["avatar"],
                class: ["ltr:pl-1.5", "rtl:pr-1.5"],
            },
            {
                variant: "pill",
                size: ["sm"],
                icon: ["trailing"],
                class: ["ltr:pr-1.5", "rtl:pl-1.5"],
            },
            {
                variant: "pill",
                size: ["md"],
                icon: ["trailing"],
                class: ["ltr:pr-2", "rtl:pl-2"],
            },
            {
                variant: "pill",
                size: ["lg"],
                icon: ["trailing"],
                class: ["ltr:pr-2.5", "rtl:pl-2.5"],
            },
            {
                variant: "pill",
                type: "color",
                size: ["sm"],
                icon: ["leading"],
                class: ["gap-0.5"],
            },
            {
                variant: "badge",
                size: ["sm"],
                class: ["px-1.5", "rounded-sm"],
            },
            {
                variant: "badge",
                size: ["md"],
                class: ["px-2", "rounded-md"],
            },
            {
                variant: "badge",
                size: ["lg"],
                class: ["px-2.5", "rounded-md"],
            },
            {
                variant: "badge",
                size: ["sm"],
                icon: ["avatar", "leading"],
                class: ["ltr:pl-1", "rtl:pr-1"],
            },
            {
                variant: "badge",
                size: ["md"],
                icon: ["avatar", "leading"],
                class: ["ltr:pl-1.5", "rtl:pr-1.5"],
            },
            {
                variant: "badge",
                size: ["lg"],
                icon: ["avatar", "leading"],
                class: ["ltr:pl-2", "rtl:pr-2"],
            },
            {
                variant: "badge",
                size: ["sm"],
                icon: ["trailing"],
                class: ["ltr:pr-1", "rtl:pl-1"],
            },
            {
                variant: "badge",
                size: ["md"],
                icon: ["trailing"],
                class: ["ltr:pr-1.5", "rtl:pl-1.5"],
            },
            {
                variant: "badge",
                size: ["lg"],
                icon: ["trailing"],
                class: ["ltr:pr-2", "rtl:pl-2"],
            },
            {
                size: ["sm"],
                icon: ["close"],
                class: ["ltr:pr-[3px]", "rtl:pl-[3px]"],
            },
            {
                size: ["md"],
                icon: ["close"],
                class: ["ltr:pr-1", "rtl:pl-1"],
            },
            {
                size: ["lg"],
                icon: ["close"],
                class: ["ltr:pr-1.5", "rtl:pl-1.5"],
            },
            {
                size: ["sm"],
                icon: ["only"],
                class: ["p-[5px]", "w-[22px]"],
            },
            {
                size: ["md"],
                icon: ["only"],
                class: ["p-1.5", "w-[24px]"],
            },
            {
                size: ["lg"],
                icon: ["only"],
                class: ["p-2", "w-[28px]"],
            },
            ..._colors,
        ],
        defaultVariants: {
            variant: "badge",
            type: "full",
            color: "gray",
            size: "md",
            icon: false,
            number: false,
            as: "div",
        },
    }
);
